import React from 'react';
import {Linking, Platform, Pressable, View} from 'react-native';

import Text from '/component/base/Text';
import {BiometricsTestIds} from '/constant/TestIDs';
import {HAS_OPTED_INTO_BIOMETRICS} from '/enum/storage.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {useStorage} from '/util/async-storage/useStorage';
import testProperties from '/util/testing/testProperties';

import {styles} from './FaceIDModal.style';

interface FaceIDModalProps {
  onClose: () => void;
  NotnowButton: () => void;
  biometricsOptIn: boolean;
}

const FaceIDModal: React.FC<FaceIDModalProps> = ({
  onClose,
  NotnowButton,
  biometricsOptIn,
}) => {
  const {ts} = useTranslation('FaceIDModal');
  const {setValue: setBiometricsOptInValue} = useStorage({
    key: HAS_OPTED_INTO_BIOMETRICS,
    defaultValue: false,
  });

  return (
    <View
      style={styles.modal}
      {...testProperties(BiometricsTestIds.enableBiometricsModal)}>
      <View style={styles.content}>
        {Platform.OS === 'ios' ? (
          <Text
            style={styles.header}
            isBold
            {...testProperties(BiometricsTestIds.enableBiometricsModalHeader)}>
            {!biometricsOptIn
              ? ts('enable face ID', 'Please enable Face ID in system settings')
              : ts(
                  'disable face ID',
                  'Please disable Face ID in system settings',
                )}
          </Text>
        ) : (
          <Text
            style={styles.header}
            isBold
            {...testProperties(BiometricsTestIds.enableBiometricsModalHeader)}>
            {!biometricsOptIn
              ? ts(
                  'enable touch ID',
                  'Please enable Touch ID in system settings',
                )
              : ts(
                  'disable touch ID',
                  'Please disable Touch ID in system settings',
                )}
          </Text>
        )}

        {Platform.OS === 'ios' ? (
          <Text
            style={styles.text}
            {...testProperties(
              BiometricsTestIds.enableBiometricsModalDescription,
            )}>
            {!biometricsOptIn
              ? ts(
                  'gotosetting',
                  `Go to settings and enable \n Face ID for “OPTAVIA Coach”`,
                )
              : ts(
                  'gotosetting',
                  `Go to settings and disable \n Face ID for “OPTAVIA Coach”`,
                )}
          </Text>
        ) : (
          <Text
            style={styles.text}
            {...testProperties(
              BiometricsTestIds.enableBiometricsModalDescription,
            )}>
            {!biometricsOptIn
              ? ts(
                  'gotosetting',
                  `Go to settings and enable \n Touch ID for “OPTAVIA Coach”`,
                )
              : ts(
                  'gotosetting',
                  `Go to settings and disable \n Touch ID for “OPTAVIA Coach”`,
                )}
          </Text>
        )}

        <View style={styles.buttonsContainer}>
          <Pressable
            style={styles.useBiometricsButton}
            accessibilityRole="button"
            accessible={true}
            accessibilityLabel="Open system settings"
            onPress={async () => {
              try {
                await Linking.openSettings();
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to open settings:', error);
              }
              setBiometricsOptInValue(biometricsOptIn);
              onClose();
            }}>
            <Text
              style={styles.blueButtonText}
              isBold
              {...testProperties(
                BiometricsTestIds.enableBiometricsModalSystemSettingsBtn,
              )}>
              {ts('openSystemSettings', 'Open system settings')}
            </Text>
          </Pressable>
          <Pressable
            style={styles.notNowButton}
            accessibilityRole="button"
            accessibilityLabel="Not now"
            accessible={true}
            onPress={() => {
              NotnowButton();
            }}>
            <Text
              style={styles.buttonText}
              isBold
              {...testProperties(
                BiometricsTestIds.enableBiometricsModalNotNowBtn,
              )}>
              {ts('notNow', 'Not now')}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

export default FaceIDModal;
